import React, { useState, useEffect } from "react";
import { FileText } from "lucide-react";
import { motion } from "framer-motion";

import AssignmentCard from "./AssignmentCard";
import { useCourse } from "../../../../context/CourseContext";
import { getAllCourseAssignments } from "../../../../services/assignment.service";

import {
  card,
  cardTitle,
  bodySecondary,
  emptyState,
  dividerInner,
  iconSize,
  motionStaggerChild,
} from "../../../../lib/design-system";
import { cn } from "../../../../lib/utils";

const AssignmentsList = ({ setSelectedOption, courseId }) => {
  const [assignments, setAssignments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAssignments = async () => {
    try {
      setIsLoading(true);
      const data = await getAllCourseAssignments({ courseID: courseId });
      setAssignments(data.assignments);
    } catch (err) {
      console.error("Error fetching assignments:", err);
      setError("Failed to load assignments. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (courseId) {
      fetchAssignments();
    }
  }, [courseId]);

  if (isLoading) {
    return (
      <div className={cn(card, "overflow-hidden")}>
        <div className={cn("p-6 border-b flex justify-between items-center", dividerInner)}>
          <div className="flex items-center space-x-3">
            <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
            <div className="h-4 w-28 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
          </div>
          <div className="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
        </div>
        <div className="p-6 space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="flex overflow-hidden rounded-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 animate-pulse"
            >
              <div className="w-1 flex-shrink-0 bg-gray-200 dark:bg-gray-700" />
              <div className="flex-1 p-4 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="h-4 w-2/3 bg-gray-200 dark:bg-gray-700 rounded-sm" />
                  <div className="h-6 w-20 bg-gray-100 dark:bg-gray-700/60 rounded-sm flex-shrink-0" />
                </div>
                <div className="h-3 w-full bg-gray-100 dark:bg-gray-700/60 rounded-sm" />
                <div className="pt-2 border-t border-gray-200 dark:border-gray-700 flex gap-4">
                  <div className="h-3 w-24 bg-gray-100 dark:bg-gray-700/60 rounded-sm" />
                  <div className="h-3 w-16 bg-gray-100 dark:bg-gray-700/60 rounded-sm" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-500 dark:text-red-400 bg-white dark:bg-gray-800 rounded-sm border border-red-200 dark:border-red-800">
        {error}
      </div>
    );
  }

  return (
    <div className={cn(card, "overflow-hidden")}>
      <div className={cn("p-6 border-b flex justify-between items-center", dividerInner)}>
        <div className="flex items-center space-x-3">
          <FileText className={cn(iconSize.lg, "text-accent1")} />
          <h2 className={cardTitle}>Assignments</h2>
        </div>
        <button
          onClick={() => { setSelectedOption("Graded"); }}
          className="text-accent1 hover:text-accent1/80 font-medium transition-colors duration-200 text-sm"
        >
          View All
        </button>
      </div>

      <div className="p-6 space-y-3">
        {assignments?.length > 0 ? (
          assignments.map((assignment, index) => (
            <motion.div
              key={assignment._id}
              {...motionStaggerChild(index)}
              whileHover={{ y: -1 }}
            >
              <AssignmentCard
                assignment={assignment}
                courseID={courseId}
              />
            </motion.div>
          ))
        ) : (
          <div className={cn(emptyState)}>
            <FileText className={cn(iconSize.empty, "mx-auto mb-3 text-gray-400 dark:text-gray-500")} />
            <p className={cn(cardTitle, "mb-1")}>No assignments available</p>
            <p className={cn(bodySecondary, "mt-1")}>New assignments will appear here when they are created.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssignmentsList;
